import React from 'react';
import { connect } from 'react-redux';
import { EmailNotificationSettings } from '../../../../contracts';
import { personalDetailsSelectors } from '../../../PersonalDetails/ducks';
import Checkbox from '../../../../shared/components/Checkbox';
import Toggle from '../Toggle';
import { notificationSettingsOperations, notificationSettingsSelectors } from '../../ducks';
import {
  NotificationsWrapper,
  Header,
  TitleWrapper,
  Title,
  Subtitle,
  EditLink,
  Description,
  Options,
  Option,
  OptionTitleWrapper,
  OptionTitle,
  CheckboxWrapper,
  BottomBorder
} from '../ShowNotificationSettings.styles';
import { SUCCESS_MESSAGES } from '../../constants';
import { getUpdateEmailNotificationBody } from '../../functions';
import { sharedOperations } from '../../../../shared/ducks';
import type { RootState } from '../../../../store/types';

type OwnProps = {
  trackEventable: (message: boolean, customAttributes: Record<string, string>) => void;
};

type StateProps = {
  emailAddress: string;
  isSubscribedGlobal: boolean;
  isSubscribedToAlerts: boolean;
  isSubscribedToEvents: boolean;
  isSubscribedToOffers: boolean;
  isSubscribedToPromotions: boolean;
  isUpdatingEmailNotifications: boolean;
  mayEditPersonalDetails: boolean;
};

type DispatchProps = {
  navigateToAccountPage: (url: string) => void;
  updateEmailNotifications: (
    body: EmailNotificationSettings,
    message: string,
    originalState: EmailNotificationSettings
  ) => void;
};

function EmailNotifications({
  trackEventable,
  emailAddress,
  isSubscribedToAlerts,
  isSubscribedToEvents,
  isSubscribedToPromotions,
  isSubscribedToOffers,
  isSubscribedGlobal,
  updateEmailNotifications,
  isUpdatingEmailNotifications,
  navigateToAccountPage,
  mayEditPersonalDetails
}: OwnProps & StateProps & DispatchProps) {
  const emailState = {
    isSubscribedToAlerts,
    isSubscribedToEvents,
    isSubscribedToPromotions,
    isSubscribedToOffers,
    isSubscribedGlobal
  };

  const setEmailNotifications = (emailNotificationToUpdate: string) => {
    let emailNotification = {};
    let toastMessage = '';
    const emailOriginalState = { ...emailState };

    switch (emailNotificationToUpdate) {
      case 'isSubscribedToAlerts':
        emailNotification = { isSubscribedToAlerts: !isSubscribedToAlerts };
        toastMessage = !isSubscribedToAlerts
          ? SUCCESS_MESSAGES.ALERTS_SUBSCRIBE
          : SUCCESS_MESSAGES.ALERTS_UNSUBSCRIBE;
        break;
      case 'isSubscribedToEvents':
        emailNotification = { isSubscribedToEvents: !isSubscribedToEvents };
        toastMessage = !isSubscribedToEvents
          ? SUCCESS_MESSAGES.EVENTS_SUBSCRIBE
          : SUCCESS_MESSAGES.EVENTS_UNSUBSCRIBE;
        break;
      case 'isSubscribedToPromotions':
        emailNotification = { isSubscribedToPromotions: !isSubscribedToPromotions };
        toastMessage = !isSubscribedToPromotions
          ? SUCCESS_MESSAGES.PROMOS_SUBSCRIBE
          : SUCCESS_MESSAGES.PROMOS_UNSUBSCRIBE;
        break;
      case 'isSubscribedToOffers':
        emailNotification = { isSubscribedToOffers: !isSubscribedToOffers };
        toastMessage = !isSubscribedToOffers
          ? SUCCESS_MESSAGES.OFFERS_SUBSCRIBE
          : SUCCESS_MESSAGES.OFFERS_UNSUBSCRIBE;
        break;
      default:
        break;
    }

    updateEmailNotifications(
      Object.assign(emailState, emailNotification),
      toastMessage,
      emailOriginalState
    );
  };

  const setAllEmailNotifications = () => {
    if (isSubscribedGlobal) {
      updateEmailNotifications(
        getUpdateEmailNotificationBody('unsubscribe'),
        SUCCESS_MESSAGES.EMAIL_UNSUBSCRIBE,
        emailState
      );
      return;
    }

    updateEmailNotifications(
      getUpdateEmailNotificationBody('subscribe'),
      SUCCESS_MESSAGES.EMAIL_SUBSCRIBE,
      emailState
    );
  };

  const handleEditLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const personalInformationUrl = '/personal-details/personal-information';
    navigateToAccountPage(personalInformationUrl);
  };

  const concentTypeChanges = isSubscribedGlobal
    ? [
        isSubscribedToAlerts && 'email-saved-search-and-inventory-alerts',
        isSubscribedToPromotions && 'email-promotions-and-exclusive-updates',
        isSubscribedToOffers && 'email-product-tops-and-special-offers',
        isSubscribedToEvents && 'email-events-and-research-surveys'
      ]
        .filter((item) => item)
        .join(', ')
    : 'email-saved-search-and-inventory-alerts, email-promotions-and-exclusive-updates, email-product-tops-and-special-offers, email-events-and-research-surveys';

  return (
    <NotificationsWrapper margin="22px 0 0" data-testid="email-notification-section">
      <Header>
        <TitleWrapper>
          <Title>Email Notifications</Title>
          <Subtitle className="has-bottom-margin" data-qa="email-subtitle">
            {emailAddress}
            {mayEditPersonalDetails && (
              <EditLink data-qa="email-edit-link" href="" onClick={handleEditLinkClick}>
                {' '}
                EDIT
              </EditLink>
            )}
          </Subtitle>
          <Description>
            Select the email content you're interested in and let us help you find your ride.
          </Description>
        </TitleWrapper>
        <Toggle
          dataQa="email-toggle"
          value={isSubscribedGlobal}
          onClick={() => {
            trackEventable(!isSubscribedGlobal, { consentType: concentTypeChanges });
            setAllEmailNotifications();
          }}
          disabled={isUpdatingEmailNotifications}
        />
      </Header>
      <Options>
        <Option>
          <OptionTitleWrapper>
            <OptionTitle>Saved Search & Inventory Alerts</OptionTitle>
            <Description>
              Get personalized vehicle recommendations, price alerts and updates that are just for
              you.
            </Description>
          </OptionTitleWrapper>
          <CheckboxWrapper>
            <Checkbox
              dataQa="alerts-checkbox"
              value={isSubscribedToAlerts}
              onClick={() => {
                trackEventable(!isSubscribedToAlerts, {
                  consentType: 'email-saved-search-and-inventory-alerts'
                });
                setEmailNotifications('isSubscribedToAlerts');
              }}
              disabled={isUpdatingEmailNotifications}
            />
          </CheckboxWrapper>
        </Option>
        <Option>
          <OptionTitleWrapper>
            <OptionTitle>Promotions & Exclusive Updates</OptionTitle>
            <Description>
              Never miss Great Deals on vehicles and access special Carvana content.
            </Description>
          </OptionTitleWrapper>
          <CheckboxWrapper>
            <Checkbox
              dataQa="updates-checkbox"
              value={isSubscribedToPromotions}
              onClick={() => {
                trackEventable(!isSubscribedToPromotions, {
                  consentType: 'email-promotions-and-exclusive-updates'
                });
                setEmailNotifications('isSubscribedToPromotions');
              }}
              disabled={isUpdatingEmailNotifications}
            />
          </CheckboxWrapper>
        </Option>
        <Option>
          <OptionTitleWrapper>
            <OptionTitle>Product Tips & Special Offers</OptionTitle>
            <Description>
              Learn about all things Carvana, like buying, selling and financing. Plus, get special
              offers that are catered to you!
            </Description>
          </OptionTitleWrapper>
          <CheckboxWrapper>
            <Checkbox
              dataQa="offers-checkbox"
              value={isSubscribedToOffers}
              onClick={() => {
                trackEventable(!isSubscribedToOffers, {
                  consentType: 'email-product-tops-and-special-offers'
                });
                setEmailNotifications('isSubscribedToOffers');
              }}
              disabled={isUpdatingEmailNotifications}
            />
          </CheckboxWrapper>
        </Option>
        <Option>
          <OptionTitleWrapper>
            <OptionTitle>Events & Research Surveys</OptionTitle>
            <Description>
              Stay connected to hear about events, participate in research surveys and more.
            </Description>
          </OptionTitleWrapper>
          <CheckboxWrapper>
            <Checkbox
              dataQa="surveys-checkbox"
              value={isSubscribedToEvents}
              onClick={() => {
                trackEventable(!isSubscribedToEvents, {
                  consentType: 'email-events-and-research-surveys'
                });
                setEmailNotifications('isSubscribedToEvents');
              }}
              disabled={isUpdatingEmailNotifications}
            />
          </CheckboxWrapper>
        </Option>
      </Options>
      <BottomBorder />
    </NotificationsWrapper>
  );
}

const mapDispatchToProps = {
  updateEmailNotifications: notificationSettingsOperations.updateEmailNotifications,
  navigateToAccountPage: sharedOperations.navigateToAccountPage
};

const mapStateToProps = (state: RootState) => ({
  emailAddress: personalDetailsSelectors.getEmailAddress(state),
  isSubscribedToAlerts: notificationSettingsSelectors.getIsSubscribedToAlerts(state),
  isSubscribedToEvents: notificationSettingsSelectors.getIsSubscribedToEvents(state),
  isSubscribedToPromotions: notificationSettingsSelectors.getIsSubscribedToPromotions(state),
  isSubscribedToOffers: notificationSettingsSelectors.getIsSubscribedToOffers(state),
  isSubscribedGlobal: notificationSettingsSelectors.getIsSubscribedGlobal(state),
  isUpdatingEmailNotifications:
    notificationSettingsSelectors.getIsUpdatingEmailNotifications(state),
  mayEditPersonalDetails: personalDetailsSelectors.getMayEditPersonalDetails(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(EmailNotifications);
